import { useCallback, useEffect, useState } from 'react';
import {
  FiArrowLeft,
  FiCheck,
  FiDollarSign,
  FiDownload,
  FiEdit,
  FiFileText,
  FiHome,
  FiPlus, FiTrash2,
  FiUser,
  FiXCircle
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clientAPI, saleAPI } from '../../services/api';
import SaleFormModal from './SaleFormModal';

const WORKFLOW_STEPS = [
  { key: 'prospect', label: '1. Négociation' },
  { key: 'offer_made', label: '2. Offre Déposée' },
  { key: 'offer_accepted', label: '3. Offre Acceptée' },
  { key: 'compromis_signed', label: '4. Compromis Signé' },
  { key: 'conditions_cleared', label: '5. Conditions Levées' },
  { key: 'deed_signed', label: '6. Acte Notarié' },
  { key: 'completed', label: '7. Clôturée' },
];

const SaleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Modal Offre
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    buyer_client_id: '',
    offered_price: '',
    financing_type: 'cash',
    validity_date: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
    conditions_precedent: '',
  });

  // Modal Document
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docForm, setDocForm] = useState({
    document_type: 'titre_foncier',
    title: '',
    file_url: '',
  });

  const fetchSale = useCallback(async () => {
    try {
      const res = await saleAPI.getById(id);
      setSale(res.data);
    } catch {
      toast.error('Erreur lors du chargement de la transaction');
      navigate('/sales');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSale();
    clientAPI.list({ limit: 100 }).then(r => setClients(r.data.data || r.data || [])).catch(() => {});
  }, [fetchSale]);

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await saleAPI.downloadCompromisPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Compromis-Vente-${sale.sale_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Compromis PDF téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await saleAPI.update(id, { status: newStatus });
      toast.success(`Statut mis à jour : ${newStatus}`);
      fetchSale();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur mise à jour statut');
    }
  };

  const handleAddOffer = async (e) => {
    e.preventDefault();
    if (!offerForm.buyer_client_id || !offerForm.offered_price) return toast.warn('Champs obligatoires manquants');
    try {
      await saleAPI.addOffer(id, offerForm);
      toast.success('Offre d\'achat enregistrée avec succès !');
      setIsOfferModalOpen(false);
      setOfferForm({
        buyer_client_id: '',
        offered_price: '',
        financing_type: 'cash',
        validity_date: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
        conditions_precedent: '',
      });
      fetchSale();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleOfferStatus = async (offerId, status) => {
    try {
      await saleAPI.updateOfferStatus(id, offerId, { status });
      toast.success(status === 'accepted' ? 'Offre acceptée et prix répercuté sur la vente !' : `Offre ${status}`);
      fetchSale();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur mise à jour offre');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await saleAPI.deleteOffer(id, offerId);
      toast.success('Offre supprimée');
      fetchSale();
    } catch (err) {
      toast.error('Erreur suppression offre');
    }
  };

  const handleAddDoc = async (e) => {
    e.preventDefault();
    if (!docForm.title || !docForm.file_url) return toast.warn('Titre et URL du fichier requis');
    try {
      await saleAPI.addDocument(id, docForm);
      toast.success('Document foncier ajouté avec succès');
      setIsDocModalOpen(false);
      setDocForm({ document_type: 'titre_foncier', title: '', file_url: '' });
      fetchSale();
    } catch (err) {
      toast.error('Erreur ajout document');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document foncier ?')) return;
    try {
      await saleAPI.deleteDocument(id, docId);
      toast.success('Document supprimé');
      fetchSale();
    } catch (err) {
      toast.error('Erreur suppression document');
    }
  };

  if (loading || !sale) {
    return <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" /></div>;
  }

  const currentStepIdx = WORKFLOW_STEPS.findIndex(s => s.key === sale.status);

  return (
    <div style={{ padding: '24px', maxWidth: 1250, margin: '0 auto' }}>
      {/* ── Top Bar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/sales')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiArrowLeft /> Retour
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Transaction #{sale.sale_number}</h1>
              <span style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                background: sale.transaction_nature === 'resale' ? '#ede9fe' : '#dcfce7',
                color: sale.transaction_nature === 'resale' ? '#7c3aed' : '#166534',
              }}>
                {sale.transaction_nature === 'resale' ? 'Revente Immobilière' : 'Vente Directe'}
              </span>
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {sale.property_title} — {sale.property_neighborhood ? `${sale.property_neighborhood}, ` : ''}{sale.property_city || 'Bamako'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={handleDownloadPDF} disabled={downloadingPDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiDownload /> {downloadingPDF ? 'Génération...' : 'Avant-Contrat / Compromis (PDF)'}
          </button>
          <button className="btn btn-primary" onClick={() => setIsEditModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiEdit /> Modifier
          </button>
        </div>
      </div>

      {/* ── Workflow Progress Bar ── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 24, borderRadius: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
          PROGRESSION DE LA TRANSACTION :
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', overflowX: 'auto', paddingBottom: 6 }}>
          {WORKFLOW_STEPS.map((step, idx) => {
            const isCompleted = idx <= currentStepIdx;
            const isCurrent = idx === currentStepIdx;
            return (
              <div 
                key={step.key} 
                onClick={() => handleUpdateStatus(step.key)}
                style={{ 
                  textAlign: 'center', flex: 1, minWidth: 100, cursor: 'pointer',
                  opacity: isCompleted ? 1 : 0.45,
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '0 auto 6px',
                  background: isCurrent ? 'var(--primary, #2563eb)' : (isCompleted ? '#16a34a' : '#cbd5e1'),
                  color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, boxShadow: isCurrent ? '0 0 0 4px #dbeafe' : 'none',
                }}>
                  {isCompleted && !isCurrent ? '✓' : idx + 1}
                </div>
                <div style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? 'var(--primary, #2563eb)' : 'inherit' }}>
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main Details Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 24 }}>
        {/* Foncier & Bien */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiHome color="#2563eb" /> Bien & Régime Foncier Malien
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Bien :</span>
              <strong>{sale.property_title}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Type de Régime :</span>
              <span style={{ fontWeight: 600, color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: 4 }}>
                {sale.land_title_type ? sale.land_title_type.toUpperCase() : 'TITRE FONCIER'}
              </span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>N° de Titre / Référence :</span>
              <strong style={{ fontFamily: 'monospace', color: '#1e293b' }}>{sale.land_title_number || 'En cours'}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Superficie :</span>
              <span>{sale.land_area || sale.property_surface || '-'} m²</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Adresse :</span>
              <span>{sale.property_address}, {sale.property_city || 'Bamako'}</span>
            </div>
          </div>
        </div>

        {/* Données Financières */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiDollarSign color="#16a34a" /> Données Financières & Commission
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Prix de Mise en Vente :</span>
              <strong>{parseFloat(sale.listed_price || 0).toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Prix Convenu / Négocié :</span>
              <strong style={{ color: '#16a34a', fontSize: 15 }}>
                {sale.agreed_sale_price ? `${parseFloat(sale.agreed_sale_price).toLocaleString('fr-FR')} XOF` : 'En négociation'}
              </strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Acompte / Séquestre :</span>
              <span>{parseFloat(sale.deposit_amount || 0).toLocaleString('fr-FR')} XOF</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Commission Agence ({sale.agency_commission_rate || 5}%) :</span>
              <strong style={{ color: '#2563eb' }}>{parseFloat(sale.agency_commission_amount || 0).toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Prise en charge com. :</span>
              <span style={{ textTransform: 'capitalize' }}>{sale.commission_payer || 'Vendeur'}</span>
            </div>
            {sale.transaction_nature === 'resale' && (
              <div style={{ background: '#f0fdf4', padding: '8px 10px', borderRadius: 6, border: '1px solid #bbf7d0', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#166534' }}>
                  Achat initial : {parseFloat(sale.acquisition_price || 0).toLocaleString('fr-FR')} XOF
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>
                  Plus-value nette générée : +{parseFloat(sale.capital_gain || 0).toLocaleString('fr-FR')} XOF
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Parties & Notaire */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiUser color="#0284c7" /> Parties & Notaire Instrumentaire
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Vendeur :</span>
              <strong>{sale.seller_first_name ? `${sale.seller_first_name} ${sale.seller_last_name} (${sale.seller_phone || '-'})` : 'Non assigné'}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Acquéreur :</span>
              <strong>{sale.buyer_first_name ? `${sale.buyer_first_name} ${sale.buyer_last_name} (${sale.buyer_phone || '-'})` : 'En recherche'}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Étude Notariale :</span>
              <span>{sale.notary_name || 'À désigner'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Tél / Email Notaire :</span>
              <span>{sale.notary_phone || '-'} {sale.notary_email ? `· ${sale.notary_email}` : ''}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Date Acte Notarié :</span>
              <span>{sale.deed_signature_date ? new Date(sale.deed_signature_date).toLocaleDateString('fr-FR') : 'À fixer'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Offres d'Achat & Négociations ── */}
      <div className="card" style={{ padding: '20px', borderRadius: 10, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              💼 Offres d'Achat & Négociations ({sale.offers?.length || 0})
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Propositions d'acquisition déposées par les acquéreurs potentiels.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setIsOfferModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Déposer une Offre
          </button>
        </div>

        {(!sale.offers || sale.offers.length === 0) ? (
          <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg, #f8fafc)', borderRadius: 8 }}>
            Aucune offre déposée pour le moment.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sale.offers.map(offer => (
              <div key={offer.id} style={{
                padding: '14px 16px', borderRadius: 8, border: '1px solid var(--border, #e2e8f0)',
                background: offer.status === 'accepted' ? '#f0fdf4' : '#ffffff',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 14 }}>{offer.buyer_first_name} {offer.buyer_last_name}</strong>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>({offer.buyer_phone || offer.buyer_email || 'Acheteur'})</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      background: offer.status === 'accepted' ? '#dcfce7' : (offer.status === 'rejected' ? '#fee2e2' : '#dbeafe'),
                      color: offer.status === 'accepted' ? '#166534' : (offer.status === 'rejected' ? '#991b1b' : '#1e40af'),
                    }}>
                      {offer.status === 'accepted' ? 'Acceptée' : (offer.status === 'rejected' ? 'Rejetée' : 'En attente')}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Financement : <strong style={{ color: '#334155' }}>{offer.financing_type}</strong> · Validité : {new Date(offer.validity_date).toLocaleDateString('fr-FR')}
                    {offer.conditions_precedent && <span> · Conditions : {offer.conditions_precedent}</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Montant Proposé</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {parseFloat(offer.offered_price).toLocaleString('fr-FR')} XOF
                    </div>
                  </div>

                  {offer.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button 
                        className="btn btn-sm" 
                        title="Accepter cette offre"
                        onClick={() => handleOfferStatus(offer.id, 'accepted')}
                        style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <FiCheck /> Accepter
                      </button>
                      <button 
                        className="btn btn-sm btn-outline" 
                        title="Rejeter"
                        onClick={() => handleOfferStatus(offer.id, 'rejected')}
                        style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                      >
                        <FiXCircle />
                      </button>
                    </div>
                  )}

                  <button 
                    onClick={() => handleDeleteOffer(offer.id)}
                    style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', padding: 4 }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Pièces & Documents Fonciers ── */}
      <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              📁 Pièces Foncières & Actes ({sale.documents?.length || 0})
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Titres fonciers, plans de bornage, extraits topographiques, compromis et actes authentiques.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setIsDocModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Ajouter un Document
          </button>
        </div>

        {(!sale.documents || sale.documents.length === 0) ? (
          <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg, #f8fafc)', borderRadius: 8 }}>
            Aucun document foncier déposé pour cette vente.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {sale.documents.map(doc => (
              <div key={doc.id} style={{
                padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border, #e2e8f0)',
                background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FiFileText size={22} color="#2563eb" />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{doc.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                      {doc.document_type.replace('_', ' ')} · {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline" style={{ padding: '4px 8px' }}>
                    <FiDownload size={13} />
                  </a>
                  <button onClick={() => handleDeleteDoc(doc.id)} style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', padding: 4 }}>
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Edition Vente */}
      <SaleFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={fetchSale}
        saleToEdit={sale}
      />

      {/* Modal Nouvelle Offre */}
      {isOfferModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 500, width: '100%', padding: 20, borderRadius: 10 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Déposer une Offre d'Achat</h3>
            <form onSubmit={handleAddOffer}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Acquéreur / Client *</label>
                <select 
                  className="form-control" 
                  value={offerForm.buyer_client_id} 
                  onChange={e => setOfferForm({ ...offerForm, buyer_client_id: e.target.value })}
                  required
                >
                  <option value="">— Sélectionner l'acheteur —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Montant Proposé (XOF) *</label>
                <input 
                  type="number" 
                  className="form-control" 
                  value={offerForm.offered_price} 
                  onChange={e => setOfferForm({ ...offerForm, offered_price: e.target.value })} 
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Mode de Financement</label>
                <select 
                  className="form-control" 
                  value={offerForm.financing_type} 
                  onChange={e => setOfferForm({ ...offerForm, financing_type: e.target.value })}
                >
                  <option value="cash">Fonds Propres (Comptant)</option>
                  <option value="bank_loan">Prêt Bancaire (Crédit)</option>
                  <option value="installment_plan">Échelonné / Traites</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Date Limite de Validité</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={offerForm.validity_date} 
                  onChange={e => setOfferForm({ ...offerForm, validity_date: e.target.value })} 
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Conditions Suspensives</label>
                <textarea 
                  rows={2}
                  className="form-control" 
                  placeholder="Ex: Obtention de crédit bancaire sous 30 jours, vérification TF..."
                  value={offerForm.conditions_precedent} 
                  onChange={e => setOfferForm({ ...offerForm, conditions_precedent: e.target.value })} 
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => setIsOfferModalOpen(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer l'offre</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nouveau Document */}
      {isDocModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 500, width: '100%', padding: 20, borderRadius: 10 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Ajouter un Document Foncier</h3>
            <form onSubmit={handleAddDoc}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Type de Pièce *</label>
                <select 
                  className="form-control" 
                  value={docForm.document_type} 
                  onChange={e => setDocForm({ ...docForm, document_type: e.target.value })}
                >
                  <option value="titre_foncier">Titre Foncier (TF)</option>
                  <option value="plan_bornage">Plan de Bornage / Extrait Topographique</option>
                  <option value="compromis_vente">Compromis / Avant-contrat</option>
                  <option value="acte_notarie">Acte Authentique Notarié</option>
                  <option value="quittance_prix">Quittance Intégrale de Paiement</option>
                  <option value="piece_identite">Pièce d'Identité Vendeur/Acheteur</option>
                  <option value="autre">Autre Document</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Intitulé du Document *</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Ex: Copie TF N° 12458 certifiée conforme"
                  value={docForm.title} 
                  onChange={e => setDocForm({ ...docForm, title: e.target.value })} 
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={labelStyle}>URL / Lien du Fichier *</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="https://..."
                  value={docForm.file_url} 
                  onChange={e => setDocForm({ ...docForm, file_url: e.target.value })} 
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => setIsDocModalOpen(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const detailRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 };
const detailLabelStyle = { color: 'var(--text-secondary, #64748b)', fontSize: 12 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
};

export default SaleDetailPage;
